// Third-party
import React from 'react';
import classNames from 'classnames';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { VALUE_ENTER, VALUE_ESCAPE } from 'keycode-js';

// Components
import Checkbox from './Checkbox';
import SearchInput from './SearchInput';
import LoadingSpinner from '../common/LoadingSpinner';
import { SecondaryLink } from '../buttons';
import RadioGroup from './RadioGroup';
import { HoldingIcon } from '../common';

// Utils
import { formatMessage, FormattedMessage } from '../../../utils/i18n';
import DropdownOptionsGroup from '@/types/dropdown-options-group';
import DropdownItem from '@/types/dropdown-item';

interface Props {
  optionsGroups: DropdownOptionsGroup[];
  value: DropdownItem[];
  multiselect?: boolean;
  onChange: (value: DropdownItem[]) => void;
  onToggleFilter: () => void;
  clearOption?: boolean;
  search?: boolean;
  loading?: boolean;
  visibleCount?: number;
}

interface State {
  search: string;
}

export enum DropdownGroupSelection {
  Checkbox = 'CHECKBOX',
  Radio = 'RADIO',
}

class FilterDropdown extends React.Component<Props, State> {
  private scrollbarRef = React.createRef<PerfectScrollbar>();

  static defaultProps = {
    itemGroups: [],
    value: [],
    onChange: () => {},
    onToggleFilter: () => {},
  };

  readonly state: State = {
    search: '',
  };

  componentDidMount() {
    this.scrollbarRef.current?.updateScroll();
  }

  clearSelection = () => {
    this.props.onChange([]);
  };

  itemIsRadio = (item: DropdownItem) =>
    this.props.optionsGroups.find(group => group.options.some(option => option.value === item.value))?.selection === DropdownGroupSelection.Radio;

  toggleItem = (item: DropdownItem, isToggled?: boolean) => {
    const toggled = isToggled ?? !this.itemIsSelected(item);

    let selected;
    if (this.props.multiselect) {
      selected = this.props.value.slice();
      if (toggled) {
        selected.push(item);
      } else {
        selected.splice(
          selected.findIndex(s => s.value === item.value),
          1,
        );
      }
    } else {
      selected = toggled ? [item] : [];
    }

    // Unselect any previously-selected radio item
    selected = selected.filter(selectedItem => !this.itemIsRadio(selectedItem));
    this.props.onChange(selected);
  };

  onSearch = (search: string) => {
    this.setState({ search });
  };

  handleKeyDown = (item: DropdownItem, e: React.KeyboardEvent) => {
    if (e.key === VALUE_ENTER) {
      e.nativeEvent.stopImmediatePropagation();
      this.onItemClick(item);
      this.props.onToggleFilter();
    }
  };

  handleEscapeKey = (e: React.KeyboardEvent) => {
    // Required for a11y https://www.w3.org/WAI/ARIA/apg/patterns/combobox/#listbox-popup-keyboard-interaction
    if (e.key === VALUE_ESCAPE) {
      e.nativeEvent.stopImmediatePropagation();
      this.props.onToggleFilter();
    }
  };

  itemIsSelected = (item: DropdownItem) => this.props.value.some(s => s.value === item.value);

  onItemClick = (item: DropdownItem) => {
    this.toggleItem(item);

    if (item.onClick) {
      item.onClick();
    }
  };

  onRadioChange = (value: DropdownItem, selected: boolean) => {
    if (selected) {
      this.props.onChange([value]);
    } else {
      this.props.onChange([]);
    }
  };

  setCheckboxLabel = (item: DropdownItem) => {
    const { type, color, label, value } = item;
    return (
      <>
        {type && color && <HoldingIcon type={type} color={color} />}
        {label || value.toString()}
      </>
    );
  };

  renderDropdownItems = (items: DropdownItem[], selectionType: DropdownGroupSelection) => {
    if (selectionType === DropdownGroupSelection.Radio) {
      const radioItems = items.map(item => ({
        label: item.label,
        value: item,
      }));

      const selected = this.props.value.length > 0 ? radioItems.find(item => item.value.value === this.props.value[0].value) : undefined;

      return <RadioGroup value={selected} onChange={(item, selected) => this.onRadioChange(item.value, selected)} items={radioItems} />;
    }

    const getChild = (item: DropdownItem) => {
      if (this.props.multiselect) {
        return (
          <Checkbox
            className={item.className}
            label={this.setCheckboxLabel(item)}
            selected={this.itemIsSelected(item)}
            onChange={(toggled: boolean) => this.toggleItem(item, toggled)}
          />
        );
      }
      return (
        <div tabIndex={0} onKeyDown={event => this.handleKeyDown(item, event)} onClick={() => this.onItemClick(item)} className={item.className}>
          <span>{item.label || item.value}</span>
        </div>
      );
    };

    return (
      <ul role="listbox">
        {items.map((item, i) => (
          <li key={i} className={classNames({ selected: this.itemIsSelected(item) })} role="option" aria-selected={this.itemIsSelected(item)}>
            {getChild(item)}
          </li>
        ))}
      </ul>
    );
  };

  renderItemGroups = () =>
    this.props.optionsGroups.map((group, index) => {
      const options = this.renderGroupOptions(group);

      if (!options) {
        return (
          <div key={index} className="filter-no-results">
            <FormattedMessage id="Search.NoSearchResults.Title" />
          </div>
        );
      }

      return (
        <div key={index}>
          {group.label && <span className="options-group-label">{group.label}</span>}
          {options}
        </div>
      );
    });

  renderGroupOptions = (group: DropdownOptionsGroup) => {
    const items = group.options.filter(item => {
      if (this.state.search === '') {
        return true;
      }

      return (item.label || item.value.toString()).toLowerCase().indexOf(this.state.search.toLowerCase()) > -1;
    });
    if (items.length === 0) {
      return null;
    }

    return this.renderDropdownItems(items, group.selection || DropdownGroupSelection.Checkbox);
  };

  hasSelection = (): boolean => this.props.value.length > 0;

  render() {
    const { search, visibleCount } = this.props;

    return (
      <div className="dropdown filter-dropdown" onKeyDown={this.handleEscapeKey}>
        {search && <SearchInput placeholder={formatMessage('Common.Search')} onChange={this.onSearch} value={this.state.search} />}

        {this.props.loading ? (
          <LoadingSpinner className="initial-loading" />
        ) : (
          <PerfectScrollbar ref={this.scrollbarRef} className="dropdown-options" style={{ '--visible-count': visibleCount } as React.CSSProperties}>
            {this.renderItemGroups()}
          </PerfectScrollbar>
        )}

        {this.props.clearOption && (
          <div className="clearFilter">
            <SecondaryLink onClick={() => this.clearSelection()} disabled={!this.hasSelection()}>
              <FormattedMessage id="Search.ClearFilters" />
            </SecondaryLink>
          </div>
        )}
      </div>
    );
  }
}

export default FilterDropdown;
